import React, { useState, useEffect } from "react";
import {
    BarChart2,
    Users,
    TrendingUp,
    Target,
    Award,
    Calendar,
    Activity,
    School,
    Map
} from "lucide-react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    RadialLinearScale,
    Title,
    Tooltip,
    Legend,
    Filler
} from "chart.js";
import { Line, Bar, Radar } from "react-chartjs-2";
import { API_BASE_URL } from "../../config";

// Register ChartJS
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    RadialLinearScale,
    Title,
    Tooltip,
    Legend,
    Filler
);

const AdminProgressDashboard = () => {
    // State
    const [period, setPeriod] = useState("week"); // week, month, quarter, year
    const [activeTab, setActiveTab] = useState("overview"); // overview, leaderboard, timeline, coverage
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [timeline, setTimeline] = useState(null);
    const [coverage, setCoverage] = useState(null);
    const [userRole, setUserRole] = useState(null);

    // Auth
    const accessToken = localStorage.getItem("accessToken");

    useEffect(() => {
        const role = localStorage.getItem("user_role");
        setUserRole(role);
        fetchData(role);
    }, [period, accessToken]);

    const fetchData = async (role) => {
        setLoading(true);
        setError(null);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const isManagement = role === "management";

            // Define endpoints based on role (Admin vs Management)
            // Note: Management endpoints are slightly different as per plan, 
            // but for this dashboard visualization we align them to the UI structure.
            // If management needs specific different views, we handle it here.

            const overviewUrl = isManagement
                ? `${API_BASE_URL}/management/progress/group-overview?period=${period}`
                : `${API_BASE_URL}/admin/progress/school-overview?period=${period}`;

            const leaderboardUrl = isManagement
                ? `${API_BASE_URL}/management/progress/comparative-analysis` // Using comparative for mgmt "leaderboard" view
                : `${API_BASE_URL}/admin/progress/teacher-leaderboard?limit=20`;

            const timelineUrl = isManagement
                ? `${API_BASE_URL}/admin/progress/aggregate-timeline?period=${period}` // Fallback/Shared for now
                : `${API_BASE_URL}/admin/progress/aggregate-timeline?period=${period}`;

            const coverageUrl = isManagement
                ? `${API_BASE_URL}/admin/progress/domain-coverage` // Fallback/Shared
                : `${API_BASE_URL}/admin/progress/domain-coverage`;

            const [overviewRes, leaderboardRes, timelineRes, coverageRes] = await Promise.all([
                fetch(overviewUrl, { headers }),
                fetch(leaderboardUrl, { headers }),
                fetch(timelineUrl, { headers }),
                fetch(coverageUrl, { headers })
            ]);

            const overviewData = await overviewRes.json();
            const leaderboardData = await leaderboardRes.json();
            const timelineData = await timelineRes.json();
            const coverageData = await coverageRes.json();

            setStats(overviewData);
            // Backend returns { teachers: [...], metric, period, total_teachers } for admin
            // or { branches: [...], metric, chart_data } for management comparative analysis
            if (Array.isArray(leaderboardData)) {
                setLeaderboard(leaderboardData);
            } else if (leaderboardData?.teachers) {
                setLeaderboard(leaderboardData.teachers);
            } else if (leaderboardData?.branches) {
                // Management comparative analysis - map branches to leaderboard format
                setLeaderboard(leaderboardData.branches.map(b => ({
                    name: b.school_name,
                    branch_name: b.school_name,
                    score: b.value,
                    rank: b.rank,
                    trend: b.change_percentage
                })));
            } else {
                setLeaderboard([]);
            }
            setTimeline(timelineData);
            setCoverage(coverageData);

        } catch (err) {
            console.error("Failed to fetch admin progress stats:", err);
            setError("Failed to load analytics data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Chart Configuration
    const chartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'bottom',
                labels: { font: { family: 'Poppins' } }
            },
            tooltip: {
                backgroundColor: 'rgba(15, 23, 42, 0.9)',
                titleFont: { family: 'Poppins', size: 13 },
                bodyFont: { family: 'Poppins', size: 12 },
                padding: 10,
                cornerRadius: 8
            }
        },
        scales: {
            x: { grid: { display: false }, ticks: { font: { family: 'Poppins' } } },
            y: { border: { dash: [4, 4] }, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { font: { family: 'Poppins' } } }
        }
    };

    // Timeline Data (Stacked Bar)
    // Backend returns: { timeline: [{date, courses, class_activities, lesson_plans, assessments}], totals }
    const timelineChartData = {
        labels: timeline?.timeline?.map(t => t.date) || [],
        datasets: [
            {
                label: 'Lesson Plans',
                data: timeline?.timeline?.map(t => t.lesson_plans) || [],
                backgroundColor: '#4365FD',
                borderRadius: 4
            },
            {
                label: 'Class Activities',
                data: timeline?.timeline?.map(t => t.class_activities) || [],
                backgroundColor: '#F17C45',
                borderRadius: 4
            },
            {
                label: 'Assessments',
                data: timeline?.timeline?.map(t => t.assessments) || [],
                backgroundColor: '#10B981',
                borderRadius: 4
            },
            {
                label: 'Courses',
                data: timeline?.timeline?.map(t => t.courses) || [],
                backgroundColor: '#8B5CF6',
                borderRadius: 4
            }
        ]
    };

    // Coverage Data (Radar)
    // Backend returns: { domains: [{domain, coverage, teachers_contributing, content_count}], radar_chart_data }
    const radarData = {
        labels: coverage?.radar_chart_data?.labels || coverage?.domains?.map(d => d.domain) || [],
        datasets: [{
            label: 'Domain Coverage %',
            data: coverage?.radar_chart_data?.values || coverage?.domains?.map(d => d.coverage) || [],
            backgroundColor: 'rgba(241, 124, 69, 0.2)',
            borderColor: '#F17C45',
            pointBackgroundColor: '#F17C45',
            pointBorderColor: '#fff',
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: '#F17C45'
        }]
    };

    if (loading) {
        return (
            <>
                <div className="min-h-screen flex items-center justify-center bg-slate-100">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <div className="min-h-screen flex items-center justify-center bg-slate-100">
                    <div className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-md">
                        <div className="text-red-500 mb-4">
                            <Target size={48} className="mx-auto" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-800 mb-2">Unable to Load Data</h3>
                        <p className="text-slate-500 mb-4">{error}</p>
                        <button
                            onClick={() => fetchData(userRole)}
                            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="min-h-screen bg-slate-50 font-[poppins] p-6 lg:p-10">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
                            <Activity className="text-orange-500" size={32} />
                            {userRole === 'management' ? 'Group Analytics' : 'School Analytics'}
                        </h1>
                        <p className="text-slate-500 mt-1">
                            {userRole === 'management'
                                ? "Comprehensive overview across all branches"
                                : "Detailed insights into school performance and teacher productivity"}
                        </p>
                    </div>

                    {/* Time Filter */}
                    <div className="bg-white p-1 rounded-xl shadow-sm border border-slate-200 flex">
                        {['week', 'month', 'quarter', 'year'].map((p) => (
                            <button
                                key={p}
                                onClick={() => setPeriod(p)}
                                className={`
                                    px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                                    ${period === p
                                        ? 'bg-orange-500 text-white shadow-md'
                                        : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'}
                                `}
                            >
                                {p.charAt(0).toUpperCase() + p.slice(1)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tabs Navigation */}
                <div className="flex border-b border-slate-200 mb-8 overflow-x-auto">
                    {[
                        { id: 'overview', icon: <Target size={18} />, label: 'Overview' },
                        { id: 'leaderboard', icon: <Award size={18} />, label: userRole === 'management' ? 'Branch Details' : 'Leaderboard' },
                        { id: 'timeline', icon: <TrendingUp size={18} />, label: 'Timeline' },
                        { id: 'coverage', icon: <Target size={18} />, label: 'Coverage' },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`
                                flex items-center gap-2 px-6 py-3 border-b-2 transition-all whitespace-nowrap
                                ${activeTab === tab.id
                                    ? 'border-orange-500 text-orange-600 font-semibold'
                                    : 'border-transparent text-slate-500 hover:text-slate-700'}
                            `}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab Content: OVERVIEW */}
                {activeTab === 'overview' && (
                    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {/* Stat Cards Row */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <StatCard
                                title="Total Teachers"
                                value={stats?.teacher_stats?.total_teachers || 0}
                                icon={<Users className="text-blue-500" />}
                                subtitle={`${stats?.teacher_stats?.active_teachers || 0} active (${stats?.teacher_stats?.activity_rate || 0}%)`}
                                color="blue"
                            />
                            <StatCard
                                title="Content Created"
                                value={
                                    (stats?.aggregate_content?.total_lesson_plans || 0) +
                                    (stats?.aggregate_content?.total_assessments || 0) +
                                    (stats?.aggregate_content?.total_courses || 0) +
                                    (stats?.aggregate_content?.total_class_activities || 0)
                                }
                                icon={<School className="text-orange-500" />}
                                subtitle={`${stats?.aggregate_content?.total_lesson_plans || 0} lessons, ${stats?.aggregate_content?.total_assessments || 0} assessments`}
                                color="orange"
                            />
                            <StatCard
                                title="Total Students"
                                value={stats?.total_students || 0}
                                icon={<Calendar className="text-emerald-500" />}
                                subtitle={`${stats?.total_classes || 0} classes`}
                                color="emerald"
                            />
                            <StatCard
                                title="Competency Coverage"
                                value={`${stats?.competency_coverage?.overall_percentage || 0}%`}
                                icon={<Target className="text-purple-500" />}
                                subtitle={`${stats?.pending_reviews || 0} pending reviews`}
                                color="purple"
                            />
                        </div>

                        {/* Recent Highlights (Mini Charts/Lists) */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-100">
                                <h3 className="font-bold text-slate-800 mb-4">Quick Velocity Check</h3>
                                <div className="h-64">
                                    <Line data={timelineChartData} options={{ ...chartOptions, maintainAspectRatio: false }} />
                                </div>
                            </div>
                            <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-100">
                                <h3 className="font-bold text-slate-800 mb-4">Top Performers Overview</h3>
                                <div className="space-y-4">
                                    {leaderboard.slice(0, 5).map((teacher, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${idx === 0 ? 'bg-yellow-500' : 'bg-slate-400'}`}>
                                                    {teacher.rank || idx + 1}
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-slate-800">{teacher.teacher_name || teacher.name}</p>
                                                    <p className="text-xs text-slate-500">{teacher.subjects?.join(', ') || teacher.branch_name || 'General'}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-bold text-slate-800">{teacher.stats?.total_content || teacher.score || 0}</p>
                                                <p className="text-xs text-slate-500">content items</p>
                                            </div>
                                        </div>
                                    ))}
                                    {leaderboard.length === 0 && (
                                        <p className="text-slate-500 text-center py-4">No teacher data available</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Tab Content: LEADERBOARD */}
                {activeTab === 'leaderboard' && (
                    <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                        <th className="p-4 font-semibold text-slate-600">Rank</th>
                                        <th className="p-4 font-semibold text-slate-600">Name</th>
                                        <th className="p-4 font-semibold text-slate-600">Lessons</th>
                                        <th className="p-4 font-semibold text-slate-600">Assessments</th>
                                        <th className="p-4 font-semibold text-slate-600">Activities</th>
                                        <th className="p-4 font-semibold text-slate-600">Total</th>
                                        <th className="p-4 font-semibold text-slate-600">Last Active</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leaderboard.map((item, idx) => (
                                        <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                                            <td className="p-4">
                                                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${idx < 3 ? 'bg-orange-100 text-orange-600' : 'bg-slate-100 text-slate-500'
                                                    }`}>
                                                    #{item.rank || idx + 1}
                                                </span>
                                            </td>
                                            <td className="p-4 font-medium text-slate-800">
                                                {item.teacher_name || item.name}
                                                {item.teacher_email && <span className="block text-xs text-slate-400">{item.teacher_email}</span>}
                                                {item.branch_name && <span className="block text-xs text-slate-400">{item.branch_name}</span>}
                                            </td>
                                            <td className="p-4 text-slate-600">{item.stats?.lesson_plans || item.lessons_count || 0}</td>
                                            <td className="p-4 text-slate-600">{item.stats?.assessments || 0}</td>
                                            <td className="p-4 text-slate-600">{item.stats?.class_activities || item.activities_count || 0}</td>
                                            <td className="p-4 font-bold text-slate-800">{item.stats?.total_content || item.score || 0}</td>
                                            <td className="p-4 text-slate-500 text-sm">
                                                {item.last_activity || '-'}
                                            </td>
                                        </tr>
                                    ))}
                                    {leaderboard.length === 0 && (
                                        <tr>
                                            <td colSpan="7" className="p-8 text-center text-slate-500">
                                                No teacher data available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Tab Content: TIMELINE */}
                {activeTab === 'timeline' && (
                    <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-100 h-[500px] animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <Bar
                            data={timelineChartData}
                            options={{
                                ...chartOptions,
                                maintainAspectRatio: false,
                                scales: { x: { stacked: true }, y: { stacked: true } }
                            }}
                        />
                    </div>
                )}

                {/* Tab Content: COVERAGE */}
                {activeTab === 'coverage' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-100 flex items-center justify-center">
                            <div className="w-full max-w-md">
                                <Radar data={radarData} options={chartOptions} />
                            </div>
                        </div>
                        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-100">
                            <h3 className="font-bold text-slate-800 mb-6">Detailed Domain Status</h3>
                            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                                {coverage?.domains?.map((domain, idx) => (
                                    <div key={idx}>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="font-medium text-slate-700">{domain.domain}</span>
                                            <span className="font-bold text-slate-800">{domain.coverage}%</span>
                                        </div>
                                        <div className="w-full bg-slate-100 rounded-full h-2">
                                            <div
                                                className="bg-orange-500 h-2 rounded-full"
                                                style={{ width: `${domain.coverage}%` }}
                                            ></div>
                                        </div>
                                        <div className="flex gap-4 mt-1 text-xs text-slate-500">
                                            <span>{domain.teachers_contributing || 0} teachers</span>
                                            <span>{domain.content_count?.lesson_plans || 0} lessons</span>
                                            <span>{domain.content_count?.assessments || 0} assessments</span>
                                        </div>
                                    </div>
                                ))}
                                {(!coverage?.domains || coverage.domains.length === 0) && (
                                    <p className="text-slate-500 text-center py-4">No domain coverage data available</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </>
    );
};

// Sub-component: Stat Card
const StatCard = ({ title, value, icon, subtitle, color }) => (
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300">
        <div className="flex justify-between items-start mb-4">
            <div className={`p-3 rounded-xl bg-${color}-50`}>
                {icon}
            </div>
        </div>
        <h4 className="text-slate-500 text-sm font-medium">{title}</h4>
        <h2 className="text-3xl font-bold text-slate-800 mt-1">{value}</h2>
        {subtitle && <p className="text-xs text-slate-400 mt-2">{subtitle}</p>}
    </div>
);

export default AdminProgressDashboard;
